import { useRouter } from "next/router";

interface Props {
  children: any;
  list: boolean;
  country: any;
  initial: boolean;
  type?: string;
  data?: any;
}

/** Normalize ProductConf `geo` whether CMS returns an object or a one-item array. */
export function resolveGeoRecord(geo: any) {
  if (!geo) return undefined;
  return Array.isArray(geo) ? geo[0] : geo;
}

type GeoRecord = {
  countryAvailability?: boolean;
  listOfCountries?: boolean | string[];
  countries?: { countryCode?: string }[];
};

function countryCodeOf(
  currentCountry: string | string[] | undefined
): string | undefined {
  return Array.isArray(currentCountry) ? currentCountry[0] : currentCountry;
}

function countryFromCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|; )country=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Rewrite query is often missing on the client; middleware also sets a country cookie. */
export function resolveCurrentCountry(
  queryCountry?: string | string[]
): string | undefined {
  return countryCodeOf(queryCountry) || countryFromCookie();
}

/** Pure geo check (no hooks) — same rules as GeoWrapper / GeoHandler. */
export function isAvailableForCountry(
  geo: GeoRecord | undefined | null,
  currentCountry: string | string[] | undefined,
  ignoreGeo?: string | string[] | undefined
) {
  if (!geo || geo.countryAvailability !== false || ignoreGeo == "true") {
    return true;
  }

  const current = countryCodeOf(currentCountry);
  const fromLinks = geo.countries?.map((entry) => entry.countryCode) ?? [];
  const fromList = Array.isArray(geo.listOfCountries) ? geo.listOfCountries : [];
  const inList = [...fromLinks, ...fromList].some((code) => code === current);

  // boolean true = hide in listed countries. never some(code !== current) — that only works for 1 country.
  if (geo.listOfCountries === true) {
    return !inList;
  }

  return inList;
}

export function GeoHandler({
  list,
  country,
  initial,
}: Pick<Props, "list" | "country" | "initial">) {
  const router = useRouter();
  const currentCountry = router.query.country;
  const ignoreGeo = router.query.ignoregeo;

  return isAvailableForCountry(
    { countryAvailability: initial, listOfCountries: list, countries: country },
    resolveCurrentCountry(currentCountry),
    ignoreGeo
  );
}

export default function GeoWrapper({
  children,
  list,
  country,
  initial,
}: Props) {
  const router = useRouter();
  const allowed = isAvailableForCountry(
    { countryAvailability: initial, listOfCountries: list, countries: country },
    resolveCurrentCountry(router.query.country),
    router.query.ignoregeo
  );

  return allowed ? children : null;
}

export function GeoWrapperPage({ list, country, initial, children }: Props) {
  const router = useRouter();
  const allowed = isAvailableForCountry(
    { countryAvailability: initial, listOfCountries: list, countries: country },
    resolveCurrentCountry(router.query.country),
    router.query.ignoregeo
  );

  return allowed ? children : <></>;
}
